import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  useTransition,
} from "react";
import {
  graphql,
  useFragment,
  usePaginationFragment,
  usePreloadedQuery,
  useQueryLoader,
} from "react-relay/hooks";

import { dataLevelEnum } from "@skydio/api_util/src/backends/cloud_api/organizations/utils";
import { Button, Input, KeyValueDetail, List, Select, useDebouncedState } from "@skydio/rivet-ui";

import MultiDockTypeFilter from "components/common/filters/DockTypeFilter";
import GroupsFilter from "components/common/filters/GroupsFilter";
import MultiOrgTierFilter from "components/common/filters/OrgTierFilter";
import MultiVehicleTypeFilter from "components/common/filters/VehicleTypeFilter";
import Pagination, { useRelayPagination } from "components/common/Pagination";
import {
  SplitStringArraySerializer,
  StringArraySerializer,
  useQueryStringState,
} from "hooks/useQueryStringState";

import AdminSearchEmptyState from "./AdminSearchEmptyState";
import CreateOrganizationModal, { CreateOrganizationModalQuery } from "./CreateOrganizationModal";

import styles from "../AdminSearchPage.module.scss";

import type { Dispatch } from "react";
import type { PreloadedQuery } from "react-relay/hooks";
import type { Key } from "@skydio/rivet-ui";
import type { CreateOrganizationModalQuery as CreateOrganizationModalQueryType } from "__generated__/CreateOrganizationModalQuery.graphql";
import type { OrganizationsPage_organization$key } from "__generated__/OrganizationsPage_organization.graphql";
import type { OrganizationsPage_organizations$key } from "__generated__/OrganizationsPage_organizations.graphql";
import type {
  datalevel,
  docktype,
  OrganizationsPageQuery,
  orgtier,
  vehicletype,
} from "__generated__/OrganizationsPageQuery.graphql";
import type { OrganizationsRefetchQuery } from "__generated__/OrganizationsRefetchQuery.graphql";

const query = graphql`
  query OrganizationsPageQuery(
    $count: Int!
    $cursor: String
    $searchTerm: String
    $groupUuids: [String!]
    $excludeGroupUuids: [String!]
    $dataLevel: datalevel
    $excludeDataLevel: datalevel
    $orgTiers: [orgtier!]
    $hasVehicleTypes: [vehicletype!]
    $noVehicleOfTypes: [vehicletype!]
    $hasDockTypes: [docktype!]
    $noDockOfTypes: [docktype!]
  ) {
    ...OrganizationsPage_organizations
      @arguments(
        count: $count
        cursor: $cursor
        searchTerm: $searchTerm
        groupUuids: $groupUuids
        excludeGroupUuids: $excludeGroupUuids
        dataLevel: $dataLevel
        excludeDataLevel: $excludeDataLevel
        orgTiers: $orgTiers
        hasVehicleTypes: $hasVehicleTypes
        noVehicleOfTypes: $noVehicleOfTypes
        hasDockTypes: $hasDockTypes
        noDockOfTypes: $noDockOfTypes
      )
  }
`;

const orgsFragment = graphql`
  fragment OrganizationsPage_organizations on Query
  @argumentDefinitions(
    count: { type: "Int", defaultValue: 10 }
    cursor: { type: "String" }
    searchTerm: { type: "String" }
    groupUuids: { type: "[String!]" }
    excludeGroupUuids: { type: "[String!]" }
    dataLevel: { type: "datalevel" }
    excludeDataLevel: { type: "datalevel" }
    orgTiers: { type: "[orgtier!]" }
    hasVehicleTypes: { type: "[vehicletype!]" }
    noVehicleOfTypes: { type: "[vehicletype!]" }
    hasDockTypes: { type: "[docktype!]" }
    noDockOfTypes: { type: "[docktype!]" }
  )
  @refetchable(queryName: "OrganizationsRefetchQuery") {
    organizations(
      searchTerm: $searchTerm
      groupUuids: $groupUuids
      excludeGroupUuids: $excludeGroupUuids
      dataLevel: $dataLevel
      excludeDataLevel: $excludeDataLevel
      orgTiers: $orgTiers
      hasVehicleTypes: $hasVehicleTypes
      noVehicleOfTypes: $noVehicleOfTypes
      hasDockTypes: $hasDockTypes
      noDockOfTypes: $noDockOfTypes
      first: $count
      after: $cursor
    ) @connection(key: "OrganizationsList_organizations") {
      totalCount
      edges {
        node {
          ...OrganizationsPage_organization
        }
      }
    }
  }
`;

const orgFragment = graphql`
  fragment OrganizationsPage_organization on Organization {
    id
    uuid
    name
    vehicles {
      totalCount
    }
    users {
      totalCount
    }
    docks {
      totalCount
    }
    accessPoints {
      totalCount
    }
  }
`;

const pageSize = 10;

const OrganiationListItem: React.FC<{
  organization: OrganizationsPage_organization$key;
}> = props => {
  const org = useFragment<OrganizationsPage_organization$key>(orgFragment, props.organization);
  return (
    <List.Item key={org.id}>
      <List.Item.Content className="flex gap-y-1">
        <List.Item.Title>
          <a href={`/o/${org.uuid}/`} className="font-thin text-current">
            {org.name}
          </a>
        </List.Item.Title>
        <List.Item.Details>
          <KeyValueDetail keyName="Vehicles">{org.vehicles?.totalCount}</KeyValueDetail>
          <KeyValueDetail keyName="Users">{org.users?.totalCount}</KeyValueDetail>
          <KeyValueDetail keyName="Docks">{org.docks?.totalCount}</KeyValueDetail>
          <KeyValueDetail keyName="External Radios">{org.accessPoints?.totalCount}</KeyValueDetail>
        </List.Item.Details>
      </List.Item.Content>
    </List.Item>
  );
};

const EMPTY_ARRAY: string[] = [];

export interface OrganizationsPageContextType {
  isCreateModalVisible: boolean;
  setIsCreateModalVisible: Dispatch<boolean>;
  setCount?: (count: number | undefined) => void;
}
export const OrganizationsPageContext = createContext<OrganizationsPageContextType | null>(null);

const OrganizationsList: React.FC<{
  queryReference: PreloadedQuery<OrganizationsPageQuery>;
}> = ({ queryReference }) => {
  const organizationsPageContext = useContext(OrganizationsPageContext);
  const [refetchRequestCount, setRefetchRequestCount] = useState(0);
  const [createOrganizationModalQueryReference, fetchCreateOrganizationModalQuery] =
    useQueryLoader<CreateOrganizationModalQueryType>(CreateOrganizationModalQuery);

  const [groups, setGroups] = useQueryStringState<string[]>(
    "g",
    EMPTY_ARRAY,
    StringArraySerializer
  );
  const [excludeGroups, setExcludeGroups] = useQueryStringState<string[]>(
    "eg",
    EMPTY_ARRAY,
    StringArraySerializer
  );
  const [dataLevel, setDataLevel] = useQueryStringState("dl", "");
  const [excludeDataLevel, setExcludeDataLevel] = useQueryStringState("edl", "");
  const [tiers, setTiers] = useQueryStringState<string[]>(
    "t",
    EMPTY_ARRAY,
    SplitStringArraySerializer
  );
  const [hasVehicleTypes, setHasVehicleTypes] = useQueryStringState<string[]>(
    "v",
    EMPTY_ARRAY,
    SplitStringArraySerializer
  );
  const [noVehicleOfTypes, setNoVehicleOfTypes] = useQueryStringState<string[]>(
    "nv",
    EMPTY_ARRAY,
    SplitStringArraySerializer
  );
  const [hasDockTypes, setHasDockTypes] = useQueryStringState<string[]>(
    "d",
    EMPTY_ARRAY,
    SplitStringArraySerializer
  );
  const [noDockOfTypes, setNoDockOfTypes] = useQueryStringState<string[]>(
    "nd",
    EMPTY_ARRAY,
    SplitStringArraySerializer
  );
  const [searchTerm, setSearchTerm] = useQueryStringState("search");
  const [localSearchTerm, setLocalSearchTerm] = useQueryStringState("olsearch", "");
  const [localSearchTermInput, handleLocalSearchTermInput] = useDebouncedState<string>(
    localSearchTerm,
    setLocalSearchTerm
  );
  const queryData = usePreloadedQuery<OrganizationsPageQuery>(query, queryReference);

  const paginationQuery = usePaginationFragment<
    OrganizationsRefetchQuery,
    OrganizationsPage_organizations$key
  >(orgsFragment, queryData);

  const [paginationProps, data, resetPage] = useRelayPagination(
    paginationQuery,
    pageSize,
    paginationQuery.data?.organizations
  );

  const resetFilters = useCallback(() => {
    setGroups(EMPTY_ARRAY);
    setExcludeGroups(EMPTY_ARRAY);
    setDataLevel("");
    setExcludeDataLevel("");
    setHasVehicleTypes(EMPTY_ARRAY);
    setTiers(EMPTY_ARRAY);
    setNoVehicleOfTypes(EMPTY_ARRAY);
    setHasDockTypes(EMPTY_ARRAY);
    setNoDockOfTypes(EMPTY_ARRAY);
    setSearchTerm("");
    setLocalSearchTerm("");
    handleLocalSearchTermInput("");
  }, [
    handleLocalSearchTermInput,
    setDataLevel,
    setExcludeDataLevel,
    setExcludeGroups,
    setGroups,
    setHasDockTypes,
    setHasVehicleTypes,
    setLocalSearchTerm,
    setNoDockOfTypes,
    setNoVehicleOfTypes,
    setSearchTerm,
    setTiers,
  ]);

  const handleDataLevelChange = useCallback(
    (dl: Key | null) => {
      setDataLevel(dl as datalevel);
    },
    [setDataLevel]
  );

  const handleExcludeDataLevelChange = useCallback(
    (edl: Key | null) => {
      setExcludeDataLevel(edl as datalevel);
    },
    [setExcludeDataLevel]
  );

  const handleCloseCreateOrganizationModal = useCallback(() => {
    organizationsPageContext?.setIsCreateModalVisible(false);
  }, [organizationsPageContext]);

  const handleOnOrganizationCreated = useCallback(() => {
    setRefetchRequestCount(refetchRequestCount + 1);
  }, [refetchRequestCount]);

  const { refetch } = paginationQuery;
  const [isPending, startTransition] = useTransition();
  useEffect(() => {
    startTransition(() => {
      resetPage();
      // Filter empty values from query parameter state arrays
      // Query parameters aren't cleared when multi-selects have no values selected, which causes
      // invalid data in the state. We need to fix this before type conversion
      const t = tiers.filter(tier => {
        return tier != "";
      });
      const v = hasVehicleTypes.filter(type => {
        return type != "";
      });
      const nv = noVehicleOfTypes.filter(type => {
        return type != "";
      });
      const d = hasDockTypes.filter(type => {
        return type != "";
      });
      const nd = noDockOfTypes.filter(type => {
        return type != "";
      });
      let count = pageSize;
      if (
        !searchTerm &&
        !localSearchTerm &&
        groups.length == 0 &&
        excludeGroups.length == 0 &&
        t.length == 0 &&
        !dataLevel &&
        !excludeDataLevel &&
        v.length == 0 &&
        nv.length == 0 &&
        d.length == 0 &&
        nd.length == 0
      )
        count = 0;

      refetch({
        count: count,
        searchTerm: localSearchTerm ? localSearchTerm : searchTerm,
        groupUuids: groups.length == 0 ? null : groups,
        excludeGroupUuids: excludeGroups.length == 0 ? null : excludeGroups,
        orgTiers: t.length == 0 ? null : (t as orgtier[]),
        dataLevel: dataLevel == "" ? null : (dataLevel as datalevel),
        excludeDataLevel: excludeDataLevel == "" ? null : (excludeDataLevel as datalevel),
        hasVehicleTypes: v.length == 0 ? null : (v as vehicletype[]),
        noVehicleOfTypes: nv.length == 0 ? null : (nv as vehicletype[]),
        hasDockTypes: d.length == 0 ? null : (d as docktype[]),
        noDockOfTypes: nd.length == 0 ? null : (nd as docktype[]),
      });
    });
  }, [
    refetch,
    resetPage,
    searchTerm,
    groups,
    excludeGroups,
    dataLevel,
    excludeDataLevel,
    tiers,
    hasVehicleTypes,
    noVehicleOfTypes,
    hasDockTypes,
    noDockOfTypes,
    refetchRequestCount,
    localSearchTerm,
  ]);

  useEffect(() => {
    if (organizationsPageContext?.isCreateModalVisible) {
      fetchCreateOrganizationModalQuery({});
    }
  }, [fetchCreateOrganizationModalQuery, organizationsPageContext?.isCreateModalVisible]);

  useEffect(() => {
    if (organizationsPageContext?.setCount) {
      organizationsPageContext?.setCount(
        paginationQuery.data.organizations?.edges.length ? paginationProps.totalCount : 0
      );
    }
  }, [
    organizationsPageContext,
    organizationsPageContext?.setCount,
    paginationProps.totalCount,
    paginationQuery.data.organizations?.edges.length,
  ]);

  useEffect(() => {
    if (localSearchTerm) {
      setSearchTerm("");
    }
  }, [localSearchTerm, setSearchTerm]);

  return (
    <>
      <div className={styles.paginationContainer}>
        <div className="flex w-full gap-4">
          <div className="flex grow flex-wrap gap-2">
            <Input
              placeholder="Search"
              className={styles.search}
              value={localSearchTermInput}
              onValueChange={handleLocalSearchTermInput}
              isClearable
              isSearch
            />
            <MultiOrgTierFilter
              paramKey="t"
              onSelect={setTiers}
              placeholder="Org Tiers"
              className="w-48"
            />
            <Select
              className="w-72"
              placeholder="Data Level"
              selectedKey={dataLevel == "" ? null : dataLevel}
              onSingleSelectionChange={handleDataLevelChange}
              isClearable={true}
            >
              {Object.entries(dataLevelEnum)
                .filter(k => {
                  return k[0] != "UNKNOWN";
                })
                .map(([key]) => {
                  return (
                    <Select.Item key={key} textValue={key}>
                      {key}
                    </Select.Item>
                  );
                })}
            </Select>
            <Select
              className="w-72"
              placeholder="Exclude Data Level"
              selectedKey={excludeDataLevel == "" ? null : excludeDataLevel}
              onSingleSelectionChange={handleExcludeDataLevelChange}
              isClearable={true}
            >
              {Object.entries(dataLevelEnum)
                .filter(k => {
                  return k[0] != "UNKNOWN";
                })
                .map(([key]) => {
                  return (
                    <Select.Item key={key} textValue={key}>
                      {key}
                    </Select.Item>
                  );
                })}
            </Select>
            <GroupsFilter value={groups} onChange={setGroups} placeholder="Feature Groups" />
            <GroupsFilter
              value={excludeGroups}
              onChange={setExcludeGroups}
              placeholder="Excluded Feature Groups"
            />
            <MultiVehicleTypeFilter
              onSelect={setHasVehicleTypes}
              paramKey="v"
              placeholder="Has Vehicle of Type"
              className="w-72"
            />
            <MultiVehicleTypeFilter
              onSelect={setNoVehicleOfTypes}
              paramKey="nv"
              placeholder="Has No Vehicle of Type"
              className="w-72"
            />
            <MultiDockTypeFilter
              onSelect={setHasDockTypes}
              paramKey="d"
              placeholder="Has Dock of Type"
              className="w-72"
            />
            <MultiDockTypeFilter
              onSelect={setNoDockOfTypes}
              paramKey="nd"
              placeholder="Has No Dock of Type"
              className="w-72"
            />
          </div>
          <div className="flex-none items-start justify-end space-x-2">
            <Button onClick={resetFilters}>Reset</Button>
            <Pagination {...paginationProps} />
          </div>
        </div>
      </div>
      {!paginationQuery.data.organizations ||
      paginationQuery.data.organizations.edges.length == 0 ? (
        <AdminSearchEmptyState
          hasResults={paginationProps.totalCount != 0}
          hasFilters={true}
          isLoading={isPending}
        />
      ) : (
        <List variant="stacked" compact>
          {isPending
            ? Array.from({ length: pageSize }, () => (
                <List.Item>
                  <List.Item.Content>
                    <List.Item.Title.Skeleton />
                    <List.Item.Description.Skeleton />
                  </List.Item.Content>
                </List.Item>
              ))
            : data !== null &&
              data.map(edge =>
                edge.node ? <OrganiationListItem organization={edge.node} /> : <></>
              )}
        </List>
      )}
      {createOrganizationModalQueryReference && organizationsPageContext && (
        <CreateOrganizationModal
          visible={organizationsPageContext.isCreateModalVisible}
          close={handleCloseCreateOrganizationModal}
          onOrganizationCreated={handleOnOrganizationCreated}
          queryRef={createOrganizationModalQueryReference}
        />
      )}
    </>
  );
};

export const OrganizationsPage = () => {
  const [queryReference, fetchOrgs] = useQueryLoader<OrganizationsPageQuery>(query);

  useEffect(() => {
    fetchOrgs({ count: 0 });
  }, [fetchOrgs]);

  return queryReference && <OrganizationsList queryReference={queryReference} />;
};
